import { Component, DestroyRef, inject, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { debounceTime, finalize, take } from 'rxjs/operators';

import { AddUserDialogComponent } from 'src/app/shared/dialogs/add-user-dialog/add-user-dialog.component';
import { ToastLevel } from 'src/app/shared/models/toast-message.module';
import { ToastService } from 'src/app/shared/services/toast.service';

import { UserService } from '../../../core/api/user.service';
import { UtilitiesService } from '../../../core/api/utlities.service';
import { CsvService } from '../../../core/helper/csv.service';
import { RoleEnum } from '../../../shared/constants/role.enums';
import { VerificationStatusEnum } from '../../../shared/constants/verfication_statuses.enums';
import { User } from '../../../shared/models';
import { SpinnerService } from '../../../shared/spinner/spinner.service';
import * as fromApp from '../../../store';
import { setDashboardFilter } from '../../../store/admin/admin.actions';
import { selectAdminFiltersAndPage } from '../../../store/admin/admin.selectors';
import { selectCurrentUser } from '../../../store/auth/auth.selectors';
import { AdminTableBase } from '../admin-table-base/admin-table-base';

@Component({
  selector: 'fc-admin-user-permissions-page',
  templateUrl: './admin-user-permissions-page.component.html',
  styleUrls: ['./admin-user-permissions-page.component.scss'],
})
export class AdminUserPermissionsPageComponent extends AdminTableBase implements OnInit {
  @ViewChild('filterEmail', { static: false }) filterEmail: any;
  @ViewChild('filterFullName', { static: false }) filterFullName: any;

  destroyRef = inject(DestroyRef);

  readonly PAGE_NAME: string = 'users';
  currentUser: User;
  dataSource: MatTableDataSource<User> = new MatTableDataSource<User>();
  RoleEnum = RoleEnum;

  roles = [
    RoleEnum.Landowner,
    RoleEnum.Admin,
    RoleEnum.SuperAdmin,
    RoleEnum.LimitedAdmin,
    RoleEnum.FinanceAdmin,
    RoleEnum.None,
  ];
  verificationStatuses = [
    VerificationStatusEnum.Verified,
    VerificationStatusEnum.Rejected,
    VerificationStatusEnum.Failed,
    VerificationStatusEnum.Pending,
  ];

  // names of columns must match backend column names
  sortBy = {
    email: this.sortStates.untouched,
    user_fullname: this.sortStates.untouched,
    role: this.sortStates.untouched,
    verified: this.sortStates.untouched,
  };

  displayedColumns: string[] = [
    'select',
    'email',
    'user_fullname',
    'isActive',
    'validated',
    'verified',
    'numSites',
    'role',
  ];

  filterValues = {
    email: '',
    role: '',
    verified: '',
    user_fullname: '',
  };

  roleQuery = '';
  verifiedQuery = '';
  fullNameQuery = '';

  applyFilterVerified$: Subject<{ event: Event; column: string }> = new Subject();
  applyFilterRole$: Subject<{ event: Event; column: string }> = new Subject();
  applyFilterFullName$: Subject<{ event: Event; column: string }> = new Subject();

  constructor(
    readonly router: Router,
    public dialog: MatDialog,
    readonly spinnerService: SpinnerService,
    readonly csvService: CsvService,
    readonly store: Store<fromApp.AppState>,
    readonly userService: UserService,
    readonly utilitiesService: UtilitiesService,
    readonly toastService: ToastService
  ) {
    super();
  }

  ngOnInit() {
    this.spinnerService.show('user-spinner');

    this.store
      .select(selectCurrentUser)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(currentUser => (this.currentUser = currentUser));

    this.applyFilterRole$
      .pipe(debounceTime(this.FILTER_DELAY), takeUntilDestroyed(this.destroyRef))
      .subscribe((eventObj: { event: any; column: string }) => {
        this.doGetUserByField(eventObj.event.value, eventObj.column);
      });

    this.applyFilterVerified$
      .pipe(debounceTime(this.FILTER_DELAY), takeUntilDestroyed(this.destroyRef))
      .subscribe((eventObj: { event: any; column: string }) => {
        this.doGetUserByField(eventObj.event.value, eventObj.column);
      });

    this.applyFilterFullName$
      .pipe(debounceTime(this.FILTER_DELAY), takeUntilDestroyed(this.destroyRef))
      .subscribe((eventObj: { event: Event; column: string }) => {
        this.doGetUserByFullName(eventObj.event, eventObj.column);
      });

    this.store
      .select(selectAdminFiltersAndPage)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(state => {
        let pageFilter = super.setUpAdminFilter(state.filters);
        this.roleQuery = pageFilter?.roleQuery || '';
        this.verifiedQuery = pageFilter?.verifiedQuery || '';
        this.fullNameQuery = pageFilter?.fullNameQuery || '';
        this.filterValues.user_fullname =
          this.fullNameQuery !== '' ? this.fullNameQuery.split(',')[1].replace(')', '') : '';

        this.filterValues.verified = this.verifiedQuery !== '' ? this.verifiedQuery.split('=')[1] : '';
        this.filterValues.role = this.roleQuery !== '' ? this.roleQuery.split('=')[1] : '';

        this.initializeFilters();
        this.queryItems();
      });
  }

  applyRoleFilter(event: any, column: string): void {
    this.applyFilterRole$.next({ event, column });
  }

  applyVerifiedFilter(event: any, column: string): void {
    this.applyFilterVerified$.next({ event, column });
  }

  applyFullNameFilter(event: any, column: string): void {
    this.applyFilterFullName$.next({ event, column });
  }

  doGetUserByField(searchString: any, field: string): void {
    if (searchString) {
      // must encode value if we want to support special characters like ; # &
      const filterValue: string = this.utilitiesService.preprocessFilterString(searchString);

      const filterStr = filterValue.length !== 0 ? `&${field}=${filterValue}` : '';

      if (field === 'role') {
        this.roleQuery = filterStr;
      } else if (field === 'verified') {
        this.verifiedQuery = filterStr;
      }
    } else if (field === 'role') {
      this.roleQuery = '';
    } else if (field === 'verified') {
      this.verifiedQuery = '';
    }
    this.store.dispatch(
      setDashboardFilter({
        filter: 'roleQuery',
        value: this.roleQuery,
        page: this.PAGE_NAME,
      })
    );
    this.store.dispatch(
      setDashboardFilter({
        filter: 'verifiedQuery',
        value: this.verifiedQuery,
        page: this.PAGE_NAME,
      })
    );
    this.resetOffsetAndCurrentPage();
    this.queryItems();
  }

  doGetUserByFullName(event: Event | MatSelectChange, field: string): void {
    const rawFilterValue = ((event as Event).target as HTMLInputElement).value;

    // must encode value if we want to support special characters like ; # &
    const filterValue: string = this.utilitiesService.preprocessFilterString(rawFilterValue);

    const filterStr = filterValue.length !== 0 ? `&${field}=last_name(contains,${filterValue})` : '';

    this.fullNameQuery = filterStr;

    this.store.dispatch(
      setDashboardFilter({
        filter: 'fullNameQuery',
        value: this.fullNameQuery,
        page: this.PAGE_NAME,
      })
    );
    this.resetOffsetAndCurrentPage();
    this.queryItems();
  }

  clearFilters(): void {
    if (this.getBaseQueryString() !== this.getFullQueryString()) {
      this.roleQuery = '';
      this.verifiedQuery = '';
      this.fullNameQuery = '';
      this.filterFullName.nativeElement.value = '';
      this.filterEmail.nativeElement.value = '';
      super.clearFilters();
      this.queryItems();
    }
  }

  getFullQueryString(): string {
    return (
      this.getBaseQueryString() +
      this.filterQuery +
      this.roleQuery +
      this.verifiedQuery +
      this.sortQuery +
      this.fullNameQuery
    );
  }

  queryItems(): void {
    // always include pagination
    this.spinnerService.show('user-spinner');
    this.userService.getFilteredUsers(this.getFullQueryString()).subscribe(userResults => {
      this.dataSource = new MatTableDataSource(userResults.results);
      this.totalItems = userResults.total;
      this.setAllPages();
      this.setStartEndIndexes();
      this.spinnerService.hide('user-spinner');
    });
  }

  openUserDialog(data?: User): void {
    const dialogRef = this.dialog.open(AddUserDialogComponent, {
      panelClass: 'fc-add-user-dialog',
      data: data || {},
      autoFocus: false,
    });
    dialogRef.afterClosed().subscribe((user: User) => {
      if (user) {
        this.queryItems();
      }
    });
  }

  resendInvite($event: any, user: User) {
    this.spinnerService.show('user-spinner');
    this.userService
      .resendInvite(user.id)
      .pipe(finalize(() => this.spinnerService.hide('user-spinner')))
      .subscribe({
        next: (result: any) => {
          if (result.status.toLowerCase() === 'ok') {
            this.toastService.sendMessage('Invite Sent', ToastLevel.SUCCESS);
          } else {
            this.toastService.sendMessage('Error Sending Invite', ToastLevel.WARN);
          }
        },
        error: (error: any) => {
          this.toastService.sendMessage('Error Sending Invite', ToastLevel.WARN);
        },
      });
  }

  getVerificationStatus(row: User) {
    if (row.isVerified) {
      return VerificationStatusEnum.Verified;
    } else if (row.identityProviderId == null) {
      return VerificationStatusEnum.Pending;
    } else if (row.rejectedBy) {
      return VerificationStatusEnum.Rejected;
    } else {
      return VerificationStatusEnum.Failed;
    }
  }

  get userIsSuperAdmin(): boolean {
    return this.currentUser ? this.currentUser.isSuperAdmin : false;
  }

  onCSVExport() {
    let csv_string =
      'User ID, Email,Created Date,First Name, Last Name,Active,Preferred Buyer,Email Validated,Identity Validated,Submitted Sites,Role,Address1, Address2, City, State, Zip, Phone\n';
    this.dataSource.data.forEach(row => {
      if (this.selection.isSelected(row)) {
        csv_string +=
          row.id +
          ',' +
          row.email +
          ',' +
          row.createdAt +
          ',' +
          (row.firstName ? row.firstName : '') +
          ',' +
          (row.lastName ? row.lastName : '') +
          ',' +
          row.isActive +
          ',' +
          (row.preferredBuyer ? row.preferredBuyer.name : '') +
          ',' +
          (row.lastLogin !== null) +
          ',' +
          this.getVerificationStatus(row) +
          ',' +
          (row.isLandowner ? row.numSubmittedSites : '') +
          ',' +
          (row.roles.length > 0 ? row.roles[0].getName() : '') +
          ',"' +
          (row.street1 ? row.street1 : '') +
          '","' +
          (row.street2 ? row.street2 : '') +
          '","' +
          (row.city ? row.city : '') +
          '","' +
          (row.state ? row.state : '') +
          '","' +
          (row.zip ? row.zip : '') +
          '","' +
          (row.phone ? row.phone : '') +
          '"\n';
      }
    });
    this.csvService.exportCSV(csv_string, 'Users');
  }
}
